// components/EditProjectModal.jsx
import { useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { create, updateProject } from '../services/project/projectService';

const schema = Yup.object({
  name: Yup.string().trim().required('Project name is required').max(100),
});

const EditProjectModal = ({ project, show, onClose, refetch = () => {} }) => {
  const parseTimeTo12Hour = (time24) => {
    if (!time24) return { hour: '9', minute: '00', ampm: 'AM' };
    const [hour24, minute] = time24.split(':');
    const hour = parseInt(hour24, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return { hour: hour12.toString(), minute, ampm };
  };

  const convertTo24Hour = (hour12, minute, ampm) => {
    if (!hour12 || !minute || !ampm) return '';
    let hour24 = parseInt(hour12, 10);
    if (ampm === 'PM' && hour24 !== 12) hour24 += 12;
    if (ampm === 'AM' && hour24 === 12) hour24 = 0;
    return `${hour24.toString().padStart(2, '0')}:${minute}`;
  };
  const startTime = parseTimeTo12Hour(project?.settings?.schedule?.start);
  const endTime = parseTimeTo12Hour(project?.settings?.schedule?.end);

  const formik = useFormik({
    initialValues: {
      name: project?.name || '',
      validation: project?.settings?.validation ?? false,
      approval: project?.settings?.approval ?? false,
      approval_validation_no_edit: project?.settings?.approval_validation_no_edit ?? false,
      validation_percent: project?.settings?.validation_percent ?? 0,
      tagOnly: project?.settings?.tagOnly ?? false,
      inventory: project?.settings?.inventory ?? false,
      scheduleEnabled: project?.settings?.schedule?.enabled ?? false,
      scheduleStartHour: startTime.hour,
      scheduleStartMinute: startTime.minute,
      scheduleStartAmpm: startTime.ampm,
      scheduleEndHour: endTime.hour,
      scheduleEndMinute: endTime.minute,
      scheduleEndAmpm: endTime.ampm,
      autoSync: project?.settings?.schedule?.autoSync ?? false,
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async ({ name }, { setSubmitting }) => {
      try {
        const hasExistingSchedule = Boolean(project?.settings?.schedule);
        const schedulePayload = formik.values.scheduleEnabled
          ? {
              enabled: true,
              start: convertTo24Hour(
                formik.values.scheduleStartHour,
                formik.values.scheduleStartMinute,
                formik.values.scheduleStartAmpm
              ),
              end: convertTo24Hour(
                formik.values.scheduleEndHour,
                formik.values.scheduleEndMinute,
                formik.values.scheduleEndAmpm
              ),
              autoSync: formik.values.autoSync,
            }
          : hasExistingSchedule
            ? {
                enabled: false,
                start: project?.settings?.schedule?.start || convertTo24Hour(
                  formik.values.scheduleStartHour,
                  formik.values.scheduleStartMinute,
                  formik.values.scheduleStartAmpm
                ),
                end: project?.settings?.schedule?.end || convertTo24Hour(
                  formik.values.scheduleEndHour,
                  formik.values.scheduleEndMinute,
                  formik.values.scheduleEndAmpm
                ),
              }
            : undefined;

        const payload = {
          name: name.trim(),
          validation: formik.values.validation,
          approval: formik.values.approval,
          approval_validation_no_edit: formik.values.approval_validation_no_edit,
          validation_percent: Number(formik.values.validation_percent ?? 0),
          tagOnly: formik.values.tagOnly,
          inventory: formik.values.inventory,
          ...(schedulePayload && { schedule: schedulePayload }),
        };
        if (project?.id) {
          const { message } = await updateProject(project.id, payload);
          toast.success(message || 'Project updated');
        } else {
          const { message } = await create(payload);
          toast.success(message || 'Project created');
        }
        refetch();
        onClose();
      } catch (error) {
        const msg =
          error?.errorMessage ||
          error?.response?.data?.errorMessage ||
          'Something went wrong';
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        document.getElementById('projectNameInput')?.focus();
      }, 100);
    }
  }, [show]);

  const unchanged =
    (project?.name || '').trim() === (formik.values.name || '').trim() &&
    (project?.settings?.validation === formik.values.validation) &&
    (project?.settings?.approval === formik.values.approval) &&
    (project?.settings?.approval_validation_no_edit === formik.values.approval_validation_no_edit) &&
    ((project?.settings?.validation_percent ?? 0) === Number(formik.values.validation_percent ?? 0)) &&
    (project?.settings?.tagOnly === formik.values.tagOnly) &&
    (project?.settings?.inventory === formik.values.inventory) &&
    (project?.settings?.schedule?.enabled === formik.values.scheduleEnabled) &&
    (!formik.values.scheduleEnabled || project?.settings?.schedule?.start === convertTo24Hour(formik.values.scheduleStartHour, formik.values.scheduleStartMinute, formik.values.scheduleStartAmpm)) &&
    (!formik.values.scheduleEnabled || project?.settings?.schedule?.end === convertTo24Hour(formik.values.scheduleEndHour, formik.values.scheduleEndMinute, formik.values.scheduleEndAmpm)) &&
    (!formik.values.scheduleEnabled || (project?.settings?.schedule?.autoSync ?? false) === formik.values.autoSync);

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{project ? 'Edit Project' : 'New Project'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3" controlId="projectNameInput">
            <Form.Label>Project Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter project name"
              {...formik.getFieldProps('name')}
              isInvalid={formik.touched.name && !!formik.errors.name}
              disabled={formik.isSubmitting}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="validationCheckbox">
            <Form.Check
              type="checkbox"
              label="Needs Validation"
              {...formik.getFieldProps('validation')}
              checked={formik.values.validation}
              disabled={formik.isSubmitting || formik.values.approval_validation_no_edit}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="approvalCheckbox">
            <Form.Check
              type="checkbox"
              label="Needs Approval"
              {...formik.getFieldProps('approval')}
              checked={formik.values.approval}
              disabled={formik.isSubmitting || formik.values.approval_validation_no_edit}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="approvalValidationNoEditCheckbox">
            <Form.Check
              type="checkbox"
              label="Approval/Validation (No edit)"
              name="approval_validation_no_edit"
              onChange={(e) => {
                const checked = e.target.checked;
                formik.setFieldValue('approval_validation_no_edit', checked);

                if (checked) {
                  formik.setFieldValue('approval', true);
                  formik.setFieldValue('validation', true);
                }
              }}
              checked={formik.values.approval_validation_no_edit}
              disabled={false}
            />
          </Form.Group>

          {formik.values.approval_validation_no_edit && (
            <Form.Group className="mb-3" controlId="validationPercentRange">
              <Form.Label>
                Validation Coverage ({Number(formik.values.validation_percent ?? 0)}%)
              </Form.Label>
              <Form.Range
                min={0}
                max={100}
                step={1}
                value={Number(formik.values.validation_percent ?? 0)}
                onChange={(e) => formik.setFieldValue('validation_percent', Number(e.target.value))}
                disabled={formik.isSubmitting}
              />
            </Form.Group>
          )}

          {formik.values.approval && (
            <Form.Group className="mb-3" controlId="tagOnlyCheckbox">
              <Form.Check
                type="checkbox"
                label="Tag Only"
                {...formik.getFieldProps('tagOnly')}
                checked={formik.values.tagOnly}
                disabled={formik.isSubmitting}
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3" controlId="inventoryCheckbox">
            <Form.Check
              type="checkbox"
              label="Inventory"
              {...formik.getFieldProps('inventory')}
              checked={formik.values.inventory}
              disabled={formik.isSubmitting}
            />
          </Form.Group>

          {formik.values.inventory && (
            <>
              <Form.Group className="mb-3" controlId="scheduleCheckbox">
                <Form.Check
                  type="checkbox"
                  label="Schedule"
                  {...formik.getFieldProps('scheduleEnabled')}
                  checked={formik.values.scheduleEnabled}
                  disabled={formik.isSubmitting}
                />
              </Form.Group>

              {formik.values.scheduleEnabled && (
                <Form.Group className="mb-3" controlId="autoSyncCheckbox">
                  <Form.Check
                    type="checkbox"
                    label="Autosync Deployed at end of schedule"
                    {...formik.getFieldProps('autoSync')}
                    checked={formik.values.autoSync}
                    disabled={formik.isSubmitting}
                  />
                </Form.Group>
              )}

              {formik.values.scheduleEnabled && (
                <div className="mb-3">
                  <Form.Label>Schedule Time Range</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Select
                      {...formik.getFieldProps('scheduleStartHour')}
                      disabled={formik.isSubmitting}
                      style={{ width: 'auto' }}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(hour => (
                        <option key={hour} value={hour}>{hour}</option>
                      ))}
                    </Form.Select>
                    <span>:</span>
                    <Form.Select
                      {...formik.getFieldProps('scheduleStartMinute')}
                      disabled={formik.isSubmitting}
                      style={{ width: 'auto' }}
                    >
                      <option value="00">00</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="45">45</option>
                    </Form.Select>
                    <Form.Select
                      {...formik.getFieldProps('scheduleStartAmpm')}
                      disabled={formik.isSubmitting}
                      style={{ width: 'auto' }}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </Form.Select>
                    <span>to</span>
                    <Form.Select
                      {...formik.getFieldProps('scheduleEndHour')}
                      disabled={formik.isSubmitting}
                      style={{ width: 'auto' }}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(hour => (
                        <option key={hour} value={hour}>{hour}</option>
                      ))}
                    </Form.Select>
                    <span>:</span>
                    <Form.Select
                      {...formik.getFieldProps('scheduleEndMinute')}
                      disabled={formik.isSubmitting}
                      style={{ width: 'auto' }}
                    >
                      <option value="00">00</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="45">45</option>
                    </Form.Select>
                    <Form.Select
                      {...formik.getFieldProps('scheduleEndAmpm')}
                      disabled={formik.isSubmitting}
                      style={{ width: 'auto' }}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </Form.Select>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={formik.isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting || unchanged || !!formik.errors.name}
            >
              {formik.isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditProjectModal;
